/*
 * Dates, times and line endings for the free-event surfaces (/start, the
 * free-event banner, the limited dashboard). Design Reqs 4 and 19:
 *
 *  - every date and time renders in the EVENT's time zone (`event_time_zone`,
 *    an IANA name) with its abbreviation — "Thu, Oct 15 at 11:59 PM PT" —
 *    never in the viewer's zone, so the host reads the same calendar day the
 *    server counts;
 *  - each date and each time is kept whole with no-break spaces, and copy
 *    never ends on a one-word line (`noWidow`).
 *
 * The year shows only when it isn't the current year in that zone.
 */

const NBSP = " ";
const LOCALE = "en-US";

const keep = (s) => s.replace(/ /g, NBSP);

const parts = (date, timeZone, options) => {
  const out = {};
  new Intl.DateTimeFormat(LOCALE, { timeZone, ...options })
    .formatToParts(date)
    .forEach(({ type, value }) => {
      out[type] = value;
    });
  return out;
};

const toDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

// "PT", not "PDT": the generic name reads right on both sides of a DST change.
// `shortGeneric` is Chrome 95+ / Safari 15.4+; older engines get "PDT".
export const zoneAbbr = (date, timeZone) => {
  for (const timeZoneName of ["shortGeneric", "short"]) {
    try {
      const p = parts(date, timeZone, { timeZoneName });
      if (p.timeZoneName) return p.timeZoneName;
    } catch (_e) {
      // unsupported option on this engine: fall through to the next
    }
  }
  return "";
};

const safeZone = (timeZone) => {
  try {
    new Intl.DateTimeFormat(LOCALE, { timeZone });
    return timeZone;
  } catch (_e) {
    return undefined; // an unknown name renders in the viewer's zone rather than throwing
  }
};

const datePart = (date, timeZone, now, { weekday = true } = {}) => {
  const p = parts(date, timeZone, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const thisYear = parts(now, timeZone, { year: "numeric" }).year;
  let s = `${p.month} ${p.day}`;
  if (weekday) s = `${p.weekday}, ${s}`;
  if (p.year !== thisYear) s = `${s}, ${p.year}`;
  return keep(s);
};

const timePart = (date, timeZone) => {
  const p = parts(date, timeZone, { hour: "numeric", minute: "2-digit", hour12: true });
  return keep(`${p.hour}:${p.minute} ${p.dayPeriod} ${zoneAbbr(date, timeZone)}`.trim());
};

// "Thu, Oct 15 at 11:59 PM PT" — the banner and dashboard dates.
export const formatEventDateTime = (iso, timeZone, now = new Date()) => {
  const date = toDate(iso);
  if (!date) return null;
  const tz = safeZone(timeZone);
  return `${datePart(date, tz, now)} at ${timePart(date, tz)}`;
};

// "Oct 13, 7:42 PM PT" — a lead's capture time.
export const formatEventStamp = (iso, timeZone, now = new Date()) => {
  const date = toDate(iso);
  if (!date) return null;
  const tz = safeZone(timeZone);
  return `${datePart(date, tz, now, { weekday: false })},${NBSP}${timePart(date, tz)}`;
};

// { dates: "Oct 13 to 15", zone: "PT" } — the event's days, for "{event_dates} ({time_zone})".
export const formatEventDays = (startIso, endIso, timeZone) => {
  const start = toDate(startIso);
  const end = toDate(endIso);
  if (!start || !end) return null;
  const tz = safeZone(timeZone);
  const a = parts(start, tz, { month: "short", day: "numeric" });
  const b = parts(end, tz, { month: "short", day: "numeric" });
  const tail = a.month === b.month ? b.day : `${b.month} ${b.day}`;
  return { dates: keep(`${a.month} ${a.day} to ${tail}`), zone: zoneAbbr(end, tz) };
};

// Whole calendar days from `now` to `iso`, counted in the event's zone.
export const calendarDaysUntil = (iso, timeZone, now = new Date()) => {
  const date = toDate(iso);
  if (!date) return null;
  const tz = safeZone(timeZone);
  const ymd = (d) => {
    const p = parts(d, tz, { year: "numeric", month: "2-digit", day: "2-digit" });
    return Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day));
  };
  return Math.round((ymd(date) - ymd(now)) / 86400000);
};

// Binds the last two words with a no-break space so no line ends on one word.
export const noWidow = (text) => {
  if (typeof text !== "string") return text;
  return text.replace(/(\S)[ ]+(\S+\s*)$/, `$1${NBSP}$2`);
};

// "0:42" — the resend and retry countdowns (Req 3).
export const formatCountdown = (seconds) => {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};
